import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaFolderOpen, FaListUl } from "react-icons/fa";

const DEFAULT_BOARD_VALUE = "CBSE";
const DEFAULT_BOARD_LABEL = "NCERT";

const TeachMeTopic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  // Load syllabus data for the Teach Me view (defaults to NCERT/CBSE)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    if (!baseUrl) {
      setFetchError("Missing API base URL. Please contact support.");
      setIsLoading(false);
      return () => {};
    }

    const fetchSyllabus = async () => {
      setIsLoading(true);
      setFetchError("");
      try {
        const { data } = await axios.get(`${baseUrl}/api/v2/syllabus`, {
          signal: controller.signal,
          params: {
            board: DEFAULT_BOARD_VALUE,
            standard: 10,
            school: "DPS",
          },
        });

        if (!isMounted) {
          return;
        }

        const parsedSubjects = Array.isArray(data)
          ? data
              .map((item) => ({
                name: item.subject,
                chapters: Array.isArray(item.chapters)
                  ? item.chapters.filter(
                      (chapter) =>
                        (chapter && chapter.docId) || chapter?.workBookUrl
                    )
                  : [],
              }))
              .filter(
                (entry) =>
                  entry.name && entry.name.trim() && entry.chapters.length > 0
              )
          : [];

        setSubjects(parsedSubjects);

        if (parsedSubjects.length > 0) {
          setSelectedSubject(parsedSubjects[0].name);
          // setSelectedChapter(parsedSubjects[0].chapters[0]?.name || "");
        } else {
          setSelectedSubject("");
          setSelectedChapter("");
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        console.error("Failed to load syllabus for Teach Me view:", error);
        setFetchError("Unable to load subjects right now. Please try again.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSyllabus();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [baseUrl]);

  // When subject changes ensure a valid chapter is always selected
  const chaptersForSelectedSubject = useMemo(() => {
    const subjectEntry = subjects.find(
      (subject) => subject.name === selectedSubject
    );
    return subjectEntry?.chapters ?? [];
  }, [subjects, selectedSubject]);

  useEffect(() => {
    if (chaptersForSelectedSubject.length === 0) {
      setSelectedChapter("");
      return;
    }

    const chapterExists = chaptersForSelectedSubject.some(
      (chapter) => chapter.name === selectedChapter
    );

    if (!chapterExists) {
      setSelectedChapter("");
    }
  }, [chaptersForSelectedSubject, selectedChapter]);

  const isTeachButtonDisabled =
    isLoading || !selectedSubject || !selectedChapter || !!fetchError;

  const handleSubjectSelect = (nextSubject) => {
    setSelectedSubject(nextSubject);
  };

  const handleChapterSelect = (nextChapter) => {
    setSelectedChapter(nextChapter);
  };

  const handleTeachMeClick = () => {
    if (isTeachButtonDisabled) {
      return;
    }

    const selectedChapterEntry = chaptersForSelectedSubject.find(
      (chapter) => chapter.name === selectedChapter
    );
    const params = new URLSearchParams({
      enableWorkbookmode: "true",
      board: DEFAULT_BOARD_VALUE,
      subject: selectedSubject,
      chapter: selectedChapter,
      from: "teachMe",
    });

    if (selectedChapterEntry?.docId) {
      params.set("docId", selectedChapterEntry.docId);
    }

    const isPracticeFlow = location.pathname.startsWith("/practice");
    const targetBasePath = isPracticeFlow ? "/practice/tutor" : "/tutor";
    navigate(`${targetBasePath}?${params.toString()}`);
  };

  const subjectOptions = subjects.map((subject) => ({
    value: subject.name,
    label: subject.name,
  }));

  const chapterOptions = chaptersForSelectedSubject.map((chapter) => ({
    value: chapter.name,
    label: chapter.name,
  }));

  const selectionSteps = [
    {
      id: "teachme-subject",
      title: "Select Subject",
      subtitle: "Choose what to learn",
      icon: <FaFolderOpen className="text-lg" />,
      options: subjectOptions,
      selectedValue: selectedSubject,
      onSelect: handleSubjectSelect,
      disabled: isLoading || subjectOptions.length === 0,
      emptyText: isLoading
        ? "Loading subjects..."
        : "No subjects available for NCERT right now",
    },
    {
      id: "teachme-chapter",
      title: "Select Chapter",
      subtitle: selectedSubject
        ? `Chapters in ${selectedSubject}`
        : "Pick a subject first",
      icon: <FaListUl className="text-lg" />,
      options: chapterOptions,
      selectedValue: selectedChapter,
      onSelect: handleChapterSelect,
      disabled: isLoading || !selectedSubject || chapterOptions.length === 0,
      emptyText: !selectedSubject
        ? "Select a subject to load chapters"
        : isLoading
        ? "Loading chapters..."
        : "No chapters available for this subject",
    },
  ];

  return (
    <div className="w-full px-4 sm:px-8 pb-6">
      <div className="bg-gradient-to-br from-[#F8F5FF] to-[#F0EFFB] rounded-3xl shadow-sm border border-[#E6E2F6] p-6 sm:p-10">
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
          <div className="flex flex-col items-center">
            <div className="rounded-3xl overflow-hidden border-4 border-white shadow-lg w-36 h-44 sm:w-48 sm:h-56">
              <img
                src="/images/tutor.png"
                alt="I'm Aanya"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">I'm Aanya</p>
              <p className="text-sm text-gray-500">Your personal study buddy</p>
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between ">
              <div>
                <p className="text-sm uppercase  text-[#0D99FF] font-semibold">
                  Teach Me a Topic
                </p>
                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900">
                  Pick what you want to learn next
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Choose a subject and chapter to quickly jump into workbook
                  mode.
                </p>
              </div>
              <div className="text-sm text-gray-500 bg-white/70 border border-white rounded-full px-4 py-1 shadow-sm">
                Board: {DEFAULT_BOARD_LABEL}
              </div>
            </div>

            <div className=" rounded-2xl ">
              {fetchError && (
                <p className="text-sm text-red-500 mb-4">{fetchError}</p>
              )}

              <TeachMeSelectionTimeline steps={selectionSteps} />

              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleTeachMeClick}
                  disabled={isTeachButtonDisabled}
                  className="inline-flex items-center justify-center rounded-full bg-[#0D99FF] px-8 py-3 text-white font-semibold shadow-md transition hover:bg-[#6149f2] disabled:opacity-60 disabled:cursor-not-allowed min-w-[150px]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading
                    </>
                  ) : (
                    "Teach Me"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TeachMeSelectionTimeline = ({ steps = [] }) => {
  if (!steps.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="relative">
        {steps.length > 1 && (
          <div className="pointer-events-none absolute left-6 top-0 bottom-0 w-px bg-primary/20" />
        )}
        <div className="space-y-6">
          {steps.map((step, index) => {
            const options = step.options ?? [];
            const hasOptions = options.length > 0;
            const isDisabled = Boolean(step.disabled);
            const isActive = Boolean(step.selectedValue);

            return (
              <div
                key={step.id ?? `${index}`}
                className="flex items-stretch gap-4"
              >
                <div className="flex flex-col items-center relative">
                  <div
                    className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 shadow-sm ${
                      isActive
                        ? "bg-primary border-primary text-white"
                        : "bg-white border-primary/30 text-primary"
                    }`}
                  >
                    {step.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-semibold">{step.title}</p>
                  {step.subtitle && (
                    <p className="text-sm text-gray-500">{step.subtitle}</p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-3">
                    {hasOptions ? (
                      options.map((option) => {
                        const optionDisabled = Boolean(option.disabled);
                        const isSelected = step.selectedValue === option.value;
                        const bubbleStateClasses = isSelected
                          ? "bg-primary border-primary text-white shadow-sm"
                          : "bg-white border-gray-300 text-gray-700 hover:border-primary hover:text-primary";

                        return (
                          <button
                            type="button"
                            key={option.value}
                            disabled={isDisabled || optionDisabled}
                            onClick={() => {
                              if (
                                isDisabled ||
                                optionDisabled ||
                                !step.onSelect
                              ) {
                                return;
                              }
                              const nextValue =
                                isSelected && step.allowClear !== false
                                  ? ""
                                  : option.value;
                              step.onSelect(nextValue);
                            }}
                            className={`px-4 py-2 rounded-full border text-sm font-medium transition-colors ${
                              isDisabled || optionDisabled
                                ? "opacity-60 cursor-not-allowed"
                                : "cursor-pointer"
                            } ${bubbleStateClasses}`}
                          >
                            {option.label}
                          </button>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        {step.emptyText || "Options will appear here"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeachMeTopic;
